import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { samplesApi, type Sample } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  Alert,
  ErrorState,
  LoadingState,
  Panel,
  StatusBadge,
} from '../../components/ui/index.js';
import { formatDateTime, formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { RecordHeader, recordLinkClass } from './RecordHeader.js';
import { SampleCustodyPanel } from './SampleCustodyPanel.js';
import { SampleDecisionPanel } from './SampleDecisionPanel.js';
import { SampleEditDialog } from './SampleEditDialog.js';
import { SampleLabelDialog } from './SampleLabelDialog.js';
import { SamplePiecesPanel } from './SamplePiecesPanel.js';
import { SampleStoragePanel } from './SampleStoragePanel.js';
import { SampleHistory } from './SampleHistory.js';
import { SampleAssignmentsPanel } from './SampleAssignmentsPanel.js';
import { SampleAttachmentsPanel } from './SampleAttachmentsPanel.js';
import { SampleReceiptCard } from './SampleReceiptCard.js';
import { SampleRequestedTestsPanel } from './SampleRequestedTestsPanel.js';
import { SampleVoidDialog } from './SampleVoidDialog.js';
import { toneForSampleStatus } from './status.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import { displayAdditionalValue, pickLabel, useSampleTypes } from './sample-type-fields.js';

/**
 * One sample: what arrived, what was decided, where it has been.
 *
 * ===========================================================================
 * THE NUMBER, THE ARRIVAL TIME AND THE WORK ORDER CANNOT BE CHANGED HERE.
 * ===========================================================================
 *
 * The number and the arrival time are what the register and the barcode were
 * built from, and a sample number is never reused; the database refuses both
 * even to a caller holding full privileges. The work order is the receipt's
 * (D223). Getting any of them wrong is a void and a re-receive — a recorded
 * act — not a quiet edit.
 *
 * EDIT SAMPLE (D222). Everything else — the material, the quantity and its
 * unit, the descriptive facts, the storage location and the requested tests —
 * is edited in ONE dialog, prefilled, saved with one request, recorded as one
 * audit entry. A failed save keeps everything typed (D228).
 *
 * "DELETE SAMPLE" IS A VOID (D211). Offered only to a holder of `samples.void`;
 * the server decides whether it may happen (D212). A voided sample opens
 * read-only, under a banner saying who voided it, when and why, and offers no
 * acceptance, assignment, test, storage, label-print or piece action. Its
 * receipt, number and history stay exactly as they were.
 *
 * THE LAYOUT (D278, the approved redesign). A record bar — Sample ID, status,
 * material, the client and project, the next step — then two columns: the
 * operational sections in their working order (requested tests, acceptance,
 * lab work, pieces, attachments, storage, custody) and a side column of facts,
 * the chain of records and the history. Every section is on the page at once;
 * the section index only scrolls to one. Nothing here changes what any
 * section does or when it is offered.
 */
export function SampleFilePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { can } = useAuth();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Arrived here straight from receiving: offer the next steps once.
  const justReceived = searchParams.get('received') === '1';
  // Arrived from a register's "Inspect": open the decision dialog once. The
  // person still chooses; nothing is decided for them.
  const wantsDecision = searchParams.get('decide') === '1';
  const [decisionRequest, setDecisionRequest] = useState(0);
  const decisionOffered = useRef(false);

  const [isEditing, setIsEditing] = useState(false);

  const [labelFor, setLabelFor] = useState<{ pieceId?: string } | null>(null);
  const [isVoiding, setIsVoiding] = useState(false);

  const sample = useQuery({
    queryKey: ['sample', id],
    queryFn: () => samplesApi.get(id as string),
  });

  const record = sample.data;
  const isVoided = record?.isVoided ?? false;
  const mayDecideNow =
    record !== undefined &&
    can(PERMISSIONS.SAMPLES_DECIDE) &&
    !record.isDisposed &&
    !isVoided &&
    record.status === 'awaiting_inspection';

  useEffect(() => {
    if (wantsDecision && mayDecideNow && !decisionOffered.current) {
      decisionOffered.current = true;
      setDecisionRequest((count) => count + 1);
    }
  }, [wantsDecision, mayDecideNow]);

  if (sample.isLoading) return <LoadingState label={t('common.loading')} />;
  if (sample.error) {
    return (
      <ErrorState error={sample.error} onRetry={() => void sample.refetch()} />
    );
  }
  if (!record) return null;

  // A disposed sample is still editable; only where it was kept is fixed (D222).
  const mayEdit = can(PERMISSIONS.SAMPLES_UPDATE) && !isVoided;
  const mayReceiveOnReceipt =
    can(PERMISSIONS.SAMPLES_RECEIVE) && record.receipt !== null && !isVoided;
  const mayVoid = can(PERMISSIONS.SAMPLES_VOID) && !isVoided;
  const mayViewWork = can(PERMISSIONS.ASSIGNMENTS_VIEW);
  const openDecision = () => setDecisionRequest((count) => count + 1);

  const sections: readonly { id: string; label: string; content: ReactNode }[] = [
    {
      id: 'requested-tests',
      label: t('sampleRequestedTests.title'),
      content: (
        <SampleRequestedTestsPanel
          sample={record}
          {...(mayEdit ? { onEdit: () => setIsEditing(true) } : {})}
        />
      ),
    },
    {
      id: 'acceptance',
      label: t('samples.decisionTitle'),
      content: <SampleDecisionPanel sample={record} openRequest={decisionRequest} />,
    },
    ...(mayViewWork
      ? [
          {
            id: 'lab-work',
            label: t('sampleWork.title'),
            content: <SampleAssignmentsPanel sample={record} />,
          },
        ]
      : []),
    {
      id: 'pieces',
      label: t('samples.piecesTitle'),
      content: (
        <SamplePiecesPanel sample={record} onLabel={(pieceId) => setLabelFor({ pieceId })} />
      ),
    },
    {
      id: 'attachments',
      label: t('sampleAttachments.title'),
      content: <SampleAttachmentsPanel sample={record} />,
    },
    {
      id: 'storage',
      label: t('samples.storageTitle'),
      content: <SampleStoragePanel sample={record} />,
    },
    {
      id: 'custody',
      label: t('samples.custodyTitle'),
      content: <SampleCustodyPanel sample={record} />,
    },
  ];

  return (
    <>
      <SampleRecordBar
        sample={record}
        actions={
          <>
            {can(PERMISSIONS.SAMPLES_VIEW) ? (
              <ActionButton priority="secondary" onPress={() => setLabelFor({})}>
                {t('samples.labelAction')}
              </ActionButton>
            ) : null}
            {mayEdit ? (
              <ActionButton priority="secondary" onPress={() => setIsEditing(true)}>
                {t('samples.edit')}
              </ActionButton>
            ) : null}
            {mayVoid ? (
              <ActionButton priority="destructive" onPress={() => setIsVoiding(true)}>
                {t('sampleVoid.action')}
              </ActionButton>
            ) : null}
            {/* The next step, and the only primary on the page. */}
            {mayDecideNow ? (
              <ActionButton priority="primary" onPress={openDecision}>
                {t('samples.recordDecision')}
              </ActionButton>
            ) : null}
          </>
        }
      />

      {justReceived ? (
        /*
          Receiving is not acceptance (D72). The decision is a separate act,
          offered in the record bar to whoever may take it — never taken for
          them.
        */
        <div className="mb-gutter">
          <Alert
            tone="success"
            title={
              record.receipt
                ? t('receiving.nextStepsTitle', {
                    number: record.sampleNumber,
                    receipt: record.receipt.receiptNumber,
                  })
                : t('receiving.nextStepsTitleNoReceipt', { number: record.sampleNumber })
            }
            description={t('receiving.nextStepsBody')}
            action={
              mayReceiveOnReceipt ? (
                <ActionButton
                  priority="secondary"
                  size="sm"
                  onPress={() =>
                    navigate(`/samples/receive?receiptId=${record.receipt?.id ?? ''}`)
                  }
                >
                  {t('receiving.anotherOnReceipt')}
                </ActionButton>
              ) : null
            }
          />
        </div>
      ) : null}

      {isVoided ? <VoidedBanner sample={record} /> : null}

      <div className="grid grid-cols-1 items-start gap-gutter lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-gutter">
          <SectionIndex sections={sections} />
          {sections.map((section) => (
            <div key={section.id} id={`sample-section-${section.id}`} className="min-w-0 scroll-mt-20">
              {section.content}
            </div>
          ))}
        </div>

        <aside className="flex min-w-0 flex-col gap-gutter">
          <SampleFacts sample={record} />
          <SampleReceiptCard sample={record} />
          <SampleHistory sampleId={record.id} />
        </aside>
      </div>

      {isEditing ? (
        <SampleEditDialog sample={record} onClose={() => setIsEditing(false)} />
      ) : null}

      <SampleLabelDialog
        sampleId={record.id}
        pieceId={labelFor?.pieceId}
        isOpen={labelFor !== null}
        onClose={() => setLabelFor(null)}
        mayPrint={!isVoided}
      />
      {mayVoid ? (
        <SampleVoidDialog
          sample={record}
          isOpen={isVoiding}
          onClose={() => setIsVoiding(false)}
        />
      ) : null}
    </>
  );
}

/**
 * The record bar: Sample ID, every state badge, the material, the sample's
 * work order with that work order's client and project — as links for
 * whoever may open them — and the actions.
 *
 * D325: the work order, client and project are the CURRENT context, read from
 * the work order the sample belongs to now. A sample received without one and
 * linked later shows them here, while its receipt card keeps the receipt as it
 * was issued. A work order without a project says "Not recorded".
 */
function SampleRecordBar({ sample, actions }: { readonly sample: Sample; readonly actions: ReactNode }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { nameOf: typeName } = useSampleTypes();
  const workOrder = sample.workOrder ?? null;
  const notRecorded = <span className="text-default-600">{t('common.notRecorded')}</span>;

  const context = workOrder
    ? [
        {
          key: 'workOrder',
          label: t('samples.contextWorkOrder'),
          value: (
            <Link
              to={`/work-orders/${workOrder.id}`}
              className={`measure ${recordLinkClass}`}
              dir="ltr"
              data-testid="sample-context-work-order"
            >
              {workOrder.workOrderNumber}
            </Link>
          ),
        },
        {
          key: 'client',
          label: t('samples.contextClient'),
          value: (
            <span data-testid="sample-context-client">
              {workOrder.client ? (
                can(PERMISSIONS.CLIENTS_VIEW) ? (
                  <Link to={`/clients/${workOrder.client.id}`} className={recordLinkClass}>
                    {workOrder.client.name}
                  </Link>
                ) : (
                  workOrder.client.name
                )
              ) : (
                notRecorded
              )}
            </span>
          ),
        },
        {
          key: 'project',
          label: t('samples.contextProject'),
          value: (
            <span data-testid="sample-context-project">
              {workOrder.project ? (
                can(PERMISSIONS.PROJECTS_VIEW) ? (
                  <Link to={`/projects/${workOrder.project.id}`} className={recordLinkClass}>
                    {workOrder.project.name}
                  </Link>
                ) : (
                  workOrder.project.name
                )
              ) : (
                notRecorded
              )}
            </span>
          ),
        },
      ]
    : [];

  return (
    <RecordHeader
      number={sample.sampleNumber}
      breadcrumbs={[
        { label: t('nav.samples'), to: '/samples' },
        { label: sample.sampleNumber },
      ]}
      badges={
        <>
          <StatusBadge
            label={t(`status.sample.${sample.status}`)}
            tone={toneForSampleStatus(sample.status)}
          />
          {sample.isDisposed ? (
            <StatusBadge label={t('samples.disposedBadge')} tone="warning" />
          ) : null}
          {sample.isVoided ? (
            <StatusBadge label={t('sampleVoid.voidedChip')} tone="danger" />
          ) : null}
        </>
      }
      title={typeName(sample.sampleType)}
      context={context}
      waitingOn={
        sample.status === 'awaiting_inspection' && !sample.isVoided && !sample.isDisposed
          ? t('samples.waitingOnDecision')
          : null
      }
      actions={actions}
    />
  );
}

/** Who voided the sample, when and why — the first thing under the record bar. */
function VoidedBanner({ sample }: { readonly sample: Sample }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  return (
    <div className="mb-gutter">
      <Alert
        tone="danger"
        title={t('sampleVoid.bannerTitle')}
        description={[
          t('sampleVoid.bannerBy', {
            name: sample.voidedBy?.displayName ?? t('common.notRecorded'),
            date: sample.voidedAt
              ? formatDateTime(sample.voidedAt, { locale })
              : t('common.notRecorded'),
          }),
          t('sampleVoid.bannerReason', { reason: sample.voidReason ?? t('common.notRecorded') }),
          t('sampleVoid.bannerBody'),
        ].join(' ')}
      />
    </div>
  );
}

/**
 * An index of the main column's sections. Every section is already on the
 * page; a button here only brings one into view, so nothing is hidden behind
 * a tab a reader might not open.
 */
function SectionIndex({
  sections,
}: {
  readonly sections: readonly { id: string; label: string }[];
}) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t('samples.sectionsNav')}
      className="flex flex-wrap gap-1 rounded-large border border-default-200 bg-white p-1"
    >
      {sections.map((section) => (
        <button
          key={section.id}
          type="button"
          className="rounded-medium px-2.5 py-1 text-caption font-medium text-default-700 outline-none transition-colors hover:bg-default-100 hover:text-default-900 focus-visible:ring-2 focus-visible:ring-primary-500"
          onClick={() =>
            document
              .getElementById(`sample-section-${section.id}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        >
          {section.label}
        </button>
      ))}
    </nav>
  );
}

/** The facts of the arrival, as recorded at the counter, in one column. */
function SampleFacts({ sample }: { readonly sample: Sample }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const timeZone = useLaboratoryTimeZone();
  const { nameOf } = useSampleTypes();
  const notRecorded = t('common.notRecorded');
  const facts: readonly { key: string; label: string; value: ReactNode }[] = [
    {
      key: 'material',
      label: t('samples.sampleTypeLabel'),
      value: nameOf(sample.sampleType),
    },
    // What the sample is (D289), straight after its material.
    {
      key: 'description',
      label: t('receiving.sampleDescription'),
      value: sample.description ?? notRecorded,
    },
    {
      key: 'quantity',
      label: t('samples.quantity'),
      // The number and the laboratory's own unit, never one alone.
      value: (
        <span className="measure" dir="ltr">
          {formatNumber(sample.quantity, { locale })} {sample.quantityUnit}
        </span>
      ),
    },
    {
      key: 'clientReference',
      label: t('receivingField.client_reference'),
      value: sample.clientReference ?? notRecorded,
    },
    {
      key: 'sampledAt',
      label: t('receivingField.sampled_at'),
      value: sample.sampledAt ? formatDateTime(sample.sampledAt, { locale }) : notRecorded,
    },
    {
      key: 'sampledBy',
      label: t('receivingField.sampled_by'),
      value: sample.sampledBy ?? notRecorded,
    },
    {
      key: 'source',
      label: t('receivingField.source_description'),
      value: sample.sourceDescription ?? notRecorded,
    },
    {
      key: 'condition',
      label: t('receivingField.condition_on_receipt'),
      value: sample.conditionOnReceipt ?? notRecorded,
    },
    // The additional details its type asked for (0024), under the labels they
    // were stored with. A field archived since still shows its value.
    ...sample.additionalFields.map((value) => ({
      key: `additional-${value.fieldId}`,
      label:
        (pickLabel(value.labelAr, value.labelEn, locale) || value.key) +
        (value.archived ? ` (${t('sampleTypes.archivedTag')})` : ''),
      value: displayAdditionalValue(value, locale, timeZone, t) || notRecorded,
    })),
    {
      key: 'notes',
      label: t('samples.notes'),
      value: sample.notes ?? notRecorded,
    },
  ];
  return (
    <Panel title={t('samples.factsTitle')} description={t('samples.deliveryPanelHint')}>
      <dl className="flex flex-col gap-2.5">
        {facts.map((fact) => (
          <div key={fact.key} className="min-w-0">
            <dt className="text-caption text-default-600">{fact.label}</dt>
            <dd className="mt-0.5 break-words text-body text-default-900">{fact.value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-stack text-caption text-default-600">
        {t('samples.identityImmutableNotice')}
      </p>
    </Panel>
  );
}
